import type { GridPaginationModel } from '@mui/x-data-grid'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import {
  GET_ACTIVATION_GROUP_SCHEDULES_API,
  getGenericActivationGroupSchedules,
} from '@/features/activationGroup/endpoints/activationGroups'
import type {
  GenericActivationGroupSchedule,
  GenericActivationGroupSchedulesPage,
} from '@/features/activationGroup/types'
import type { UseQueryOptions } from '@/types/queries'

type UseGenericActivationGroupSchedulesQueryParams = {
  activationGroupUuid: string
  pagination: GridPaginationModel
}

export type UseGenericActivationGroupSchedulesQueryResult = Omit<UseQueryResult, 'data'> & {
  activationGroupSchedules: GenericActivationGroupSchedule[] | null
  currentPage: number
  totalActivationGroupSchedules: number
}

export function useGenericActivationGroupSchedulesQuery(
  params: UseGenericActivationGroupSchedulesQueryParams,
  options?: UseQueryOptions<GenericActivationGroupSchedulesPage>,
): UseGenericActivationGroupSchedulesQueryResult {
  const { data, ...queryResult } = useQuery({
    ...options,
    queryKey: [GET_ACTIVATION_GROUP_SCHEDULES_API, params],
    queryFn: () => getGenericActivationGroupSchedules(params.activationGroupUuid, params.pagination),
  })

  return {
    ...queryResult,
    activationGroupSchedules: data?.activationGroupSchedules ?? null,
    currentPage: data?.currentPage ?? 0,
    totalActivationGroupSchedules: data?.totalActivationGroupSchedules ?? 0,
  }
}

export function useInvalidateGenericActivationGroupSchedulesQuery() {
  const queryClient = useQueryClient()

  return (activationGroupUuid: string) =>
    queryClient.invalidateQueries({ queryKey: [GET_ACTIVATION_GROUP_SCHEDULES_API, { activationGroupUuid }] })
}
